// @flow
import { Dropdown, InputField, Typeahead } from '@boi/core/lib';
import { noop } from '@boi/core/lib/utils';
import { faSearch } from '@fortawesome/free-solid-svg-icons';
import { Form, FormikProps, withFormik } from 'formik';
import PropTypes from 'prop-types';
import React, { useContext, useState } from 'react';
import * as yup from 'yup';

import { CognitoContext } from '../../CognitoUtils';
import { ERROR_TEXT_LATEST_QUOTE } from '../../constants/commonConstants';
import {
  LATEST_QUOTE_OPTION_SEARCH_USER_CONSTANT,
  LATEST_QUOTE_SEARCH_USER_CONSTANT
} from '../../constants/home';
import {
  LABELMAPING,
  PLACEHOLDERMAPING
} from '../../constants/retriveUserConstant';
import { isFieldError } from '../../helpers/FieldErrorHelper';
import { formClass, withPrefix } from '../../helpers/HtmlHelper';
import { getLatestQuoteUsers } from '../../services/common/commonServices';
import type { ApiError } from '../../types/commonTypes';
import type { MatchingUserResponseModel } from '../../types/Home/Api';
import SearchAllQuotesYupSchema from './SearchAllQuotesYupSchema';

type Props = {
  fetchQuotes: (string, string) => noop,
  setLoading: boolean => noop,
  setParentError: (?string) => noop
};

export type SearchFormType = {
  searchInput: ?string,
  dropdownValue: string
};

export const SearchCriteriaForm = (
  props: Props & FormikProps<SearchFormType>
) => {
  const [selectedBranch, setSelectedBranch] = useState<?string>(undefined);
  const [emailList, setEmailList] = useState<string[]>([]);
  const { branchList } = useContext(CognitoContext);

  const {
    errors,
    handleBlur,
    handleChange,
    touched,
    setFieldValue,
    values,
    fetchQuotes,
    setLoading,
    setParentError,
    setFieldTouched
  } = props;

  const withClass = withPrefix('c-SearchAllQuotes');

  const emailSearch = async () => {
    setParentError(undefined);
    setEmailList([]);
    setLoading(true);
    const data: MatchingUserResponseModel[] | ApiError = await getLatestQuoteUsers(values);
    setLoading(false);
    if (data.length === 0 || data.error) {
      setParentError(ERROR_TEXT_LATEST_QUOTE);
    } else if (data.length === 1) {
      fetchQuotes('email', data[0].email);
    } else {
      // $FlowFixMe
      setEmailList(data.map(({ email }: MatchingUserResponseModel) => email));
    }
  };

  const handleEmailClick = (email: string) => {
    setEmailList([]);
    fetchQuotes('email', email);
  };

  const EmailOptions = () =>
    emailList.length > 0 && (
      <ul
        className={withClass('__options')}
        id="EmailOptionsList"
        data-testid="EmailOptionsList"
      >
        {emailList.map((email: string, index: number) => (
          <li
            key={`email-key-${index}`}
            onClick={() => handleEmailClick(email)}
          >
            {email}
          </li>
        ))}
      </ul>
    );

  const DropDownSearchType = () => (
    <div className={formClass('__fieldContainer')}>
      <label
        htmlFor="searchDropdown"
        className={formClass('__fieldLabel')}
        data-testid={formClass('__searchDropdownLabel')}
      >
        Select A Search Option
      </label>
      <span className={withClass('__input')}>
        <Dropdown
          data-testid={withClass('__dropdownValue')}
          error={isFieldError('dropdownValue', touched, errors)}
          errorMessage={errors.dropdownValue}
          name="dropdownValue"
          placeholder="Type here"
          type="text"
          value={values.dropdownValue}
          onChange={(e: SyntheticEvent<HTMLInputElement>) => {
            handleChange(e);
            setFieldValue('searchInput', '');
            setFieldTouched('searchInput', false);
          }}
          onBlur={(e: string) => {
            handleBlur(e);
          }}
        >
          {LATEST_QUOTE_SEARCH_USER_CONSTANT.map(
            (title: string, index: number) => {
              return (
                <option
                  value={LATEST_QUOTE_OPTION_SEARCH_USER_CONSTANT[index]}
                  key={`${index}_${title}`}
                >
                  {LATEST_QUOTE_SEARCH_USER_CONSTANT[index]}
                </option>
              );
            }
          )}
        </Dropdown>
      </span>
    </div>
  );

  const emailSection = () => (
    <>
      <InputField
        error={isFieldError('searchInput', touched, errors)}
        errorMessage={errors.searchInput}
        name="searchInput"
        onChange={(e: SyntheticEvent<HTMLInputElement>) => {
          handleChange(e);
          setEmailList([]);
        }}
        placeholder={PLACEHOLDERMAPING[`${values.dropdownValue}`]}
        touched={touched.searchInput}
        type="text"
        value={values.searchInput}
        suffix={faSearch}
        onKeyDown={(event: SyntheticKeyboardEvent<HTMLInputElement>) => {
          if (event.key === 'Enter' && !errors?.searchInput) {
            emailSearch();
          }
        }}
        onClickIcon={() => {
          if (!errors?.searchInput) {
            emailSearch();
          }
        }}
      />
      <EmailOptions />
    </>
  );

  const branchSection = () => (
    <Typeahead
      data={branchList}
      error={isFieldError('searchInput', touched, errors)}
      errorMessage={errors.searchInput}
      name="searchInput"
      placeholder={PLACEHOLDERMAPING[`${values.dropdownValue}`]}
      touched={touched.searchInput}
      onChange={(e: ?HTMLOptionElement) => {
        setSelectedBranch(e?.value);
        if (e?.value) {
          // $FlowFixMe
          fetchQuotes('branch', e.value);
        }
      }}
      value={selectedBranch}
    />
  );

  const mortgageReferenceSection = () => (
    <>
      <InputField
        error={isFieldError('searchInput', touched, errors)}
        errorMessage={errors.searchInput}
        name="searchInput"
        onChange={(e: SyntheticEvent<HTMLInputElement>) => {
          handleChange(e);
        }}
        placeholder={PLACEHOLDERMAPING[`${values.dropdownValue}`]}
        touched={touched.searchInput}
        type="text"
        value={values.searchInput}
        suffix={faSearch}
        onKeyDown={(event: SyntheticKeyboardEvent<HTMLInputElement>) => {
          if (event.key === 'Enter') {
            if (!errors?.searchInput) {
              // $FlowFixMe
              fetchQuotes('mortgage', values.searchInput);
            }
          }
        }}
        onClickIcon={() => {
          setFieldTouched('searchInput', true);
          if (!errors?.searchInput) {
            fetchQuotes('mortgage', values.searchInput);
          }
        }}
      />
    </>
  );


  function renderSection() {
    switch (values.dropdownValue) {
      case 'branch':
        return branchSection();
      case 'email':
        return emailSection();
      case 'mortgageReference':
        return mortgageReferenceSection();
      default:
        return emailSection();
    }
  }


  return (
    <Form>
      <DropDownSearchType />
      <div className={formClass('__fieldContainer')}>
        <label
          htmlFor="searchInput"
          className={formClass('__fieldLabel')}
          data-testid={formClass('__searchInputLabel')}
        >
          {LABELMAPING[`${values.dropdownValue}`]}
        </label>
        <span className={withClass('__input')}>
          {renderSection()}
        </span>
      </div>
    </Form>
  );
};

export const SearchForm = withFormik<Props, SearchFormType>({
  mapPropsToValues(): {} {
    return {
      dropdownValue: 'email',
      searchInput: ''
    };
  },
  handleSubmit(values: SearchFormType, { setSubmitting }: FormikProps) {
    setSubmitting(false);
  },
  validationSchema: yup.object().shape(SearchAllQuotesYupSchema),
  displayName: 'SearchAllQuotesForm'
})(SearchCriteriaForm);

SearchCriteriaForm.propTypes = {
  setFieldValue: PropTypes.func,
  values: PropTypes.object,
  touched: PropTypes.object,
  errors: PropTypes.object,
  setFieldTouched: PropTypes.func
};
